import { getTrend } from "../utils/block-review-helpers";

// Direction → color class. Colors themselves live in block-review.css and map to
// the design-system semantic tokens (--success / --danger / --warning / muted).
const DIR_CLASS = {
  up: "trend--up",
  down: "trend--down",
  same: "trend--same",
};

// RPE is intentionally inverted: a rising RPE means the athlete worked harder for
// the same output (fatigue signal), so up = warning and down = success.
const RPE_DIR_CLASS = {
  up: "trend--rpe-up",
  down: "trend--rpe-down",
  same: "trend--same",
};

const Glyph = ({ dir }) => {
  if (dir === "up") {
    return (
      <svg className="trend-glyph" viewBox="0 0 10 10" aria-hidden="true">
        <path d="M5 1.5L9 7H1L5 1.5Z" fill="currentColor" />
      </svg>
    );
  }
  if (dir === "down") {
    return (
      <svg className="trend-glyph" viewBox="0 0 10 10" aria-hidden="true">
        <path d="M5 8.5L1 3H9L5 8.5Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg className="trend-glyph" viewBox="0 0 10 10" aria-hidden="true">
      <rect x="1" y="3.5" width="8" height="3" rx="1.5" fill="currentColor" />
    </svg>
  );
};

/**
 * A single week-over-week change indicator. Renders nothing when there is no
 * comparable previous value (PRD §5.4 / §8).
 *
 * Pass display-ready numbers (already unit-converted/rounded for load-based
 * metrics) so the hover tooltip matches what the coach sees in the table.
 *
 * @param {number|null} curr   current week value
 * @param {number|null} prev   previous week value (same set position / metric)
 * @param {string} metric      "reps" | "load" | "rpe" | "e1rm" | "volume" | "maxWeight"
 * @param {string} [letter]    optional key shown in a chip (R / E / L) for per-set rows
 * @param {string} [label]     tooltip prefix, e.g. "Load" → "Load: 150 → 155kg"
 * @param {string} [unit]      tooltip unit suffix, e.g. "kg"
 */
function TrendIndicator({ curr, prev, metric, letter, label, unit = "" }) {
  const trend = getTrend(curr, prev, metric);
  if (!trend) return null;

  const colorClass =
    metric === "rpe" ? RPE_DIR_CLASS[trend.dir] : DIR_CLASS[trend.dir];

  const title = label ? `${label}: ${prev}${unit} → ${curr}${unit}` : undefined;

  if (letter) {
    return (
      <span className={`trend-group ${colorClass}`} title={title}>
        <span className="trend-letter">{letter}</span>
        <Glyph dir={trend.dir} />
      </span>
    );
  }

  return (
    <span className={`trend-metric ${colorClass}`} title={title}>
      <Glyph dir={trend.dir} />
    </span>
  );
}

export default TrendIndicator;
